import React from 'react';
import moment from 'moment-timezone';
import BoxBody from 'components/BoxBody';
import CoachingConstants from 'coaching/CoachingConstants';
import {
  CoachingReservation,
  CoachingTimeOff,
} from 'coaching/containers/GetAvailableReservationsContainer';
import TutoringSelfServeViewScheduleTimezoneSelector from 'coaching/components/TutoringSelfServeView/TutoringSelfServeViewSchedule/TutoringSelfServeViewScheduleTimezoneSelector';
import CoachingSelfServeTimeOffView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeTimeOffView';
import Copy from 'components/Copy';
import DateTimeAvailabilitySelect from 'components/DateTimeAvailabilitySelect';
import Image from 'components/Image';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';
import ToastActions from 'actions/ToastActions';

import StarsDoodleIllustration from 'assets/images/coaching/stars-animated-css.svg';

type Props = {
  selectedSubject: string;
  daysToShow: moment.Moment[];
  userTimezone: string;
  selectedReservation?: CoachingReservation;
  reservations?: CoachingReservation[];
  timeOff?: CoachingTimeOff[];
  loading: boolean;
  onSelectReservation: (reservation: CoachingReservation) => void;
  onSelectTimezone: (tz: string) => void;
};

export default function TutoringScheduleAvailabilitySelection(props: Props) {
  const handleSelectDate = (date: CoachingReservation) => {
    const errorToastText =
      'Oops! Looks like something went wrong - please refresh the page and try again.';
    const { reservations } = props;
    if (!reservations) {
      ToastActions.showErrorToast(errorToastText);
      console.error(
        'TutoringSelfServeViewScheduleSessionView - tried to select a date without any reservations loaded'
      );
      return;
    }

    const selectedReservation = reservations.find(res =>
      moment.utc(res.startDateTime).isSame(date.startDateTime, 'second')
    );
    if (!selectedReservation) {
      ToastActions.showErrorToast(errorToastText);
      console.error(
        'TutoringSelfServeViewScheduleSessionView - tried to select a date without a corresponding reservation'
      );
      return;
    }

    props.onSelectReservation(selectedReservation);
  };

  const renderAvailabilities = () => {
    const {
      daysToShow,
      selectedReservation,
      loading,
      userTimezone,
      reservations,
      onSelectTimezone,
    } = props;
    const dateTimeOptionsInUserTimezone =
      reservations?.map(r => ({
        ...r,
        startDateTime: r.startDateTime.tz(userTimezone),
      })) ?? [];

    if (loading) {
      return <AvailabityLoader />;
    }

    return (
      <>
        <Row size="xxxxsmall" data-test="schedule-session-view">
          <MediaObject gutter="xxxxlarge" center figure="right">
            <Text size="small" weight="semibold">
              Start date
            </Text>
            <TutoringSelfServeViewScheduleTimezoneSelector
              onSelectTimezone={onSelectTimezone}
              userTimezone={userTimezone}
            />
          </MediaObject>
        </Row>
        <Row size="small">
          <DateTimeAvailabilitySelect
            currentlyViewedDay={selectedReservation?.startDateTime?.tz(userTimezone)}
            data-test="schedule-session-date-time-select"
            box
            daysPerRow={6}
            slotsPerRow={userAgent.isDesktop() ? 4 : 3}
            value={selectedReservation}
            onChange={handleSelectDate}
            daysToShow={daysToShow}
            timezone={userTimezone}
            dateTimeOptions={dateTimeOptionsInUserTimezone}
            onEmptyDay={(day: string) => {
              if (!loading) {
                console.error(
                  `TutoringSelfServeViewScheduleSessionView - could not find any open slots on ${day} for ${props.selectedSubject}`
                );
              }
            }}
          />
        </Row>
      </>
    );
  };

  const { timeOff } = props;

  return (
    <React.Fragment>
      {renderAvailabilities()}
      {timeOff && <CoachingSelfServeTimeOffView timeOff={timeOff} />}
    </React.Fragment>
  );
}

function AvailabityLoader() {
  return (
    <Row>
      <BoxBody>
        <Text align="center">
          <Row size="xsmall">
            <Image src={StarsDoodleIllustration} />
          </Row>
          <Text weight="semibold" color="graphite">
            Checking availability
          </Text>
        </Text>
      </BoxBody>
    </Row>
  );
}

export function WaitlistCopyAndLink({
  flowVersion,
  selectedSlotStart,
  selectedSlotEnd,
}: WaitlistProps) {
  const clickEventData: WaitlistClickData = {
    flow_version: flowVersion,
    start_date_time: selectedSlotStart,
    end_date_time: selectedSlotEnd,
  };

  return (
    <Row size="small">
      <Copy align="left" size="small">
        Can’t find a time that works for you?{' '}
        <Link
          underline
          href={CoachingConstants.TUTORING_WAITLIST_SURVEY_URL}
          target="_blank"
          eventKey="coaching.self_schedule_purchase.waitlist.join.click"
          eventProperties={{
            data: JSON.stringify(clickEventData),
          }}>
          Join the waitlist
        </Link>{' '}
        and we’ll be in touch when a tutor is available.
      </Copy>
    </Row>
  );
}

type FlowVersion = 'old' | 'streamlined';

type WaitlistProps = {
  flowVersion: FlowVersion;
  selectedSlotStart: moment.Moment | null;
  selectedSlotEnd: moment.Moment | null;
};

type WaitlistClickData = {
  flow_version: FlowVersion;
  start_date_time: moment.Moment | null;
  end_date_time: moment.Moment | null;
};
